export const skillLevels = ["beginner", "intermediate", "advanced", "mixed"] as const
export const roles = ["student", "professional", "manager", "researcher", "hobbyist"] as const
export const languageProficiencies = ["native", "non-native", "simplified"] as const
export const pacing = ["slow-and-thorough", "balanced", "fast-and-dense"] as const
export const tones = ["formal", "conversational", "motivational", "strict"] as const
export const explanationDepths = ["intuitive", "conceptual", "mathematical"] as const
export const landingPageGoals = ["generate-leads", "drive-signups", "drive-sales"] as const
export const landingPageAudienceOfferTypes = [
	"beginner",
	"professional",
	"student",
	"premium",
	"high-ticket",
	"low-ticket",
] as const
export const landingPageCopyTones = ["friendly", "salesy", "authority-building"] as const
export const landingPageAdditionalPageTypes = ["about-me", "sales-page", "event-calendar", "other"] as const
export const blogPostContentTypes = [
	"educational-guide",
	"how-to",
	"tips-list",
	"case-study",
	"opinion",
	"other",
] as const
export const aiGenerationTypes = [
	"landing-page",
	"landing-page-additional-page",
	"blog-post",
	"blog-post-edit",
	"student-email",
	"course",
	"chapter",
	"course-edit",
	"website-edit",
	"ucraft-migration-page",
] as const
// Ucraft migrations run as an internal migration workflow, without creator feedback.
export const aiFeedbackGenerationTypes = aiGenerationTypes.filter(type => type !== "ucraft-migration-page")
export type AiFeedbackGenerationType = (typeof aiFeedbackGenerationTypes)[number]

export const aiGenerationCreditCosts = {
	"landing-page": 100,
	"landing-page-additional-page": 20,
	"blog-post": 20,
	"blog-post-edit": 10,
	"student-email": 10,
	course: 100,
	chapter: 20,
	"course-edit": 10,
	"website-edit": 10,
	"ucraft-migration-page": 0,
} satisfies Record<AiGenerationType, number>

const billedGenerationCosts = Object.values(aiGenerationCreditCosts).filter(cost => cost > 0)
// Creators are warned once the balance no longer covers the most expensive generation, so they can top up
// before a course or website generation is refused instead of finding out from the refusal.
export const aiCreditLowBalanceThreshold = Math.max(...billedGenerationCosts)
const cheapestBilledGenerationCost = Math.min(...billedGenerationCosts)

export type AiCreditBalanceStatus = "sufficient" | "low" | "depleted"

export function getAiCreditBalanceStatus(balance: number): AiCreditBalanceStatus {
	if (balance < cheapestBilledGenerationCost) return "depleted"
	if (balance < aiCreditLowBalanceThreshold) return "low"
	return "sufficient"
}

export const aiCreditPacks = [
	{
		id: "starter",
		title: "Starter",
		description: "For trying several AI course, chapter, and landing page generations.",
		credits: 400,
		price: { amount: 19, currency: "USD" },
	},
	{
		id: "plus",
		title: "Plus",
		description: "A better rate for creators building several courses and landing pages.",
		credits: 1200,
		price: { amount: 49, currency: "USD" },
	},
	{
		id: "pro",
		title: "Pro",
		description: "Best value for larger launches and frequent AI-assisted content work.",
		credits: 8000,
		price: { amount: 149, currency: "USD" },
	},
] as const

export type SkillLevel = (typeof skillLevels)[number]
export type Role = (typeof roles)[number]
export type LanguageProficiency = (typeof languageProficiencies)[number]
export type Pacing = (typeof pacing)[number]
export type Tone = (typeof tones)[number]
export type ExplanationDepth = (typeof explanationDepths)[number]
export type LandingPageGoal = (typeof landingPageGoals)[number]
export type LandingPageAudienceOfferType = (typeof landingPageAudienceOfferTypes)[number]
export type LandingPageCopyTone = (typeof landingPageCopyTones)[number]
export type LandingPageAdditionalPageType = (typeof landingPageAdditionalPageTypes)[number]
export type BlogPostContentType = (typeof blogPostContentTypes)[number]
export type AiGenerationType = (typeof aiGenerationTypes)[number]
export type AiCreditPack = (typeof aiCreditPacks)[number]
export type AiCreditPackId = AiCreditPack["id"]

/** How many jobs the single AI worker instance runs at once, which the API's queue wait estimate relies on. */
export const aiWorkerConcurrency = 10
